import React from 'react';
import { FiX, FiAlertTriangle, FiSend, FiCheckCircle, FiDownload, FiFileText, FiRefreshCw, FiTool } from 'react-icons/fi';
import { formatarDocumento } from '../../../utils/documento';
import styles from './Emitir.module.css';
import useEsc from '../../../hooks/useEsc';
import { pendenciasEvento } from './EventoServico';

/* Revisão como modal, acionada pelo botão "Revisar e emitir": é a última
   conferência antes de transmitir, e NFS-e autorizada não se apaga, só se
   cancela.

   Depois de autorizada, o mesmo modal vira o comprovante -- mostra o PDF na
   hora e oferece o XML. Fechar e ter que procurar a nota em outra aba só
   pra ver o que acabou de sair seria passo a troco de nada. */

function moeda(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dataBr(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).split('-');
  return `${d}/${m}/${a}`;
}

function Linha({ rotulo, valor, destaque }) {
  return (
    <div className={styles.revisaoLinha}>
      <span className={styles.revisaoRotulo}>{rotulo}</span>
      <span className={destaque ? styles.revisaoValorDestaque : styles.revisaoValor}>
        {valor === '' || valor == null ? '—' : valor}
      </span>
    </div>
  );
}

function ModalRevisao({
  prestador, dados, municipioNome, pendencias, opcoesEspeciais = [], retencoes = [], corpo = '', semPerfil = false,
  emitindo, onEmitir, resultado, pdfUrl, erroPdf, onTentarPdf, onBaixarXml, onFechar, onCorrigir,
  previaUrl, erroPrevia,
}) {
  useEsc(onFechar);
  const valor = Number(dados.valorServico || 0);
  const descInc = Number(dados.descontoIncondicionado || 0);
  const baseCalculo = Math.max(0, valor - descInc);
  const aliquota = Number(dados.aliquotaIss || 0);

  /* cada pendência diz onde se corrige: num bloco dos Parâmetros de NF
     (abre por cima e volta pra cá com tudo recarregado) ou num campo desta
     própria tela (fecha a revisão e rola até ele) */
  const bloqueios = pendencias.map(p => ({
    texto: p,
    acao: p === 'certificado digital' ? { bloco: 'certificadodigital' } : { bloco: 'prestador' },
  }));
  const add = (texto, acao) => bloqueios.push({ texto, acao });
  if (!dados.tomadorDocumento) add('tomador', { ancora: 'secao-pessoas' });
  if (semPerfil) add('perfil de tributação (obrigatório)', { bloco: 'perfis', perfil: true });
  if (!dados.codigoTributacaoNacional) add('código de tributação nacional', { bloco: 'perfis', perfil: true });
  if (dados.cTribMunExige && !dados.codigoTributacaoMunicipal) add('código complementar municipal (o município exige)', { ancora: 'secao-servico' });
  /* a Receita aceita nota sem NBS, mas a falta expõe o cliente a autuação
     -- então a trava é nossa, aqui e no backend */
  if (!dados.itemNbs) add('código NBS (do perfil de tributação)', { bloco: 'perfis', perfil: true });
  if (!dados.descricaoServico?.trim()) add('descrição do serviço', { ancora: 'secao-servico' });
  if (!(valor > 0)) add('valor do serviço', { ancora: 'secao-valores' });
  if (!dados.competencia) add('data de competência', { ancora: 'secao-valores' });
  pendenciasEvento(dados).forEach(t => add(`${t} (item 12 exige)`, { ancora: 'secao-servico' }));

  const autorizada = !!resultado?.autorizada;

  return (
    <div className={styles.modalFundo}>
      {/* tela cheia: o PDF precisa de espaço pra ser conferido de verdade */}
      <div className={styles.modalCaixa} style={{ maxWidth: 'none', width: '97vw', height: '95vh', maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <div>
            <h3 className={styles.modalTitulo}>
              {autorizada ? 'NFS-e autorizada' : 'Confira antes de emitir'}
            </h3>
            <p className={styles.modalSub}>
              {autorizada
                ? 'A nota foi transmitida e autorizada pela Receita.'
                : 'NFS-e autorizada não pode ser apagada, apenas cancelada.'}
            </p>
          </div>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {autorizada ? (
            <>
              <div className={styles.sucesso}>
                <FiCheckCircle size={15} />
                <div>
                  <strong>Nota emitida com sucesso.</strong>
                  {resultado.chaveAcesso && (
                    <div style={{ fontSize: 11.5, marginTop: 2 }}>Chave: {resultado.chaveAcesso}</div>
                  )}
                </div>
              </div>

              <div className={styles.acoesNota}>
                {pdfUrl && (
                  <a className={styles.btnSecundario} href={pdfUrl}
                    download={`NFSe-${resultado.chaveAcesso}.pdf`}>
                    <FiDownload size={13} /> Baixar PDF
                  </a>
                )}
                {resultado.nfseXml && (
                  <button type="button" className={styles.btnSecundario} onClick={onBaixarXml}>
                    <FiFileText size={13} /> Baixar XML
                  </button>
                )}
              </div>

              {pdfUrl ? (
                <iframe className={styles.visualizadorPdf} style={{ flex: 1, height: 'auto', minHeight: 420 }} src={pdfUrl} title="DANFSe" />
              ) : erroPdf ? (
                /* o PDF vem de outro serviço da Receita (ADN), que cai de vez
                   em quando. A nota já está autorizada aqui -- por isso o
                   aviso fala só do PDF, e não some com o XML nem com a chave */
                <div className={styles.avisoPdf}>
                  <FiAlertTriangle size={14} />
                  <div>
                    <strong>O PDF não veio desta vez.</strong>
                    <div style={{ marginTop: 2 }}>{erroPdf}</div>
                    {onTentarPdf && (
                      <button type="button" className={styles.btnSecundario}
                        style={{ marginTop: 8 }} onClick={onTentarPdf}>
                        <FiRefreshCw size={13} /> Tentar de novo
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <p className={styles.dica} style={{ marginTop: 10 }}>
                  Carregando o PDF da nota... Se não aparecer, use "Notas Emitidas" para baixar.
                </p>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', maxWidth: 640, overflowY: 'auto', minHeight: 0, maxHeight: '100%' }}>
              <div className={styles.revisaoGrade} style={{ gridTemplateColumns: '1fr' }}>
                <div className={styles.revisaoCartao}>
                  <h4 className={styles.revisaoTitulo}>Emitente</h4>
                  <Linha rotulo="Razão social" valor={prestador.empresa || prestador.nome} />
                  <Linha rotulo="CNPJ/CPF" valor={formatarDocumento(prestador.cnpj || prestador.cpf)} />
                  <Linha rotulo="Competência" valor={dataBr(dados.competencia)} />
                  <Linha rotulo="Nº da DPS" valor={dados.numeroDps} />
                </div>

                <div className={styles.revisaoCartao}>
                  <h4 className={styles.revisaoTitulo}>Tomador</h4>
                  <Linha rotulo="Documento" valor={formatarDocumento(dados.tomadorDocumento)} />
                  <Linha rotulo="Nome" valor={dados.tomadorNome} />
                  <Linha rotulo="E-mail" valor={dados.tomadorEmail} />
                </div>

                <div className={styles.revisaoCartao}>
                  <h4 className={styles.revisaoTitulo}>Serviço</h4>
                  <Linha rotulo="Local da prestação" valor={municipioNome} />
                  <Linha rotulo="Cód. Tributação Nacional" valor={dados.codigoTributacaoNacional} />
                  {dados.codigoTributacaoMunicipal && <Linha rotulo="Cód. complementar municipal" valor={dados.codigoTributacaoMunicipal} />}
                  <Linha rotulo="Código NBS" valor={dados.itemNbs} />
                  <Linha rotulo="Descrição" valor={dados.descricaoServico} />
                  {corpo && <Linha rotulo="Corpo da nota" valor={<span style={{ whiteSpace: 'pre-wrap' }}>{corpo}</span>} />}
                </div>

                <div className={styles.revisaoCartao}>
                  <h4 className={styles.revisaoTitulo}>Valores</h4>
                  <Linha rotulo="Valor do serviço" valor={moeda(valor)} destaque />
                  {descInc > 0 && <Linha rotulo="Desconto incondicionado" valor={`- ${moeda(descInc)}`} />}
                  {descInc > 0 && <Linha rotulo="Base de cálculo" valor={moeda(baseCalculo)} />}
                  <Linha rotulo="Alíquota ISS"
                    valor={aliquota > 0 ? `${aliquota}%` : 'apurado pelo regime'} />
                  <Linha rotulo="Retenção do ISS" valor={dados.reterIss ? 'Sim' : 'Não'} />
                  {retencoes.map(r => (
                    <Linha key={r.rotulo} rotulo={r.rotulo} valor={moeda(r.valor)} />
                  ))}
                </div>
              </div>

              {opcoesEspeciais.length > 0 && (
                <div className={styles.aviso} style={{ marginTop: 12 }}>
                  <FiAlertTriangle size={14} />
                  <div>Fora do padrão: <strong>{opcoesEspeciais.join(' · ')}</strong>.</div>
                </div>
              )}

              {bloqueios.length > 0 && (
                <div className={styles.aviso} style={{ marginTop: 12 }}>
                  <FiAlertTriangle size={14} />
                  <div style={{ flex: 1 }}>
                    <div>Falta preencher:</div>
                    {bloqueios.map(b => (
                      <div key={b.texto} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <strong style={{ flex: 1 }}>{b.texto}</strong>
                        {onCorrigir && (
                          <button type="button" className={styles.btnSecundario}
                            style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            onClick={() => onCorrigir(b.acao)}>
                            <FiTool size={12} /> {b.acao.ancora ? 'Ir ao campo' : 'Corrigir'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resultado && !autorizada && (
                <div className={styles.erroCaixa} style={{ marginTop: 12 }}>
                  <FiAlertTriangle size={15} />
                  <div style={{ minWidth: 0 }}>
                    <strong>A Receita recusou a nota</strong>
                    {resultado.mensagem && <pre className={styles.respostaBruta}>{resultado.mensagem}</pre>}
                  </div>
                </div>
              )}
            </div>

            {/* a nota como vai sair, no mesmo PDF da autorizada, com a tarja
                "apenas conferência" */}
            <div style={{ flex: '2 1 520px', display: 'flex', flexDirection: 'column', minHeight: 420 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>
                Prévia do PDF — apenas conferência, ainda não emitida
              </div>
              {previaUrl ? (
                <iframe className={styles.visualizadorPdf} style={{ flex: 1, height: 'auto' }} src={previaUrl} title="Prévia do DANFSe" />
              ) : erroPrevia ? (
                <div className={styles.avisoPdf}><FiAlertTriangle size={14} /><div>{erroPrevia}</div></div>
              ) : (
                <p className={styles.dica}>Montando a prévia...</p>
              )}
            </div>
            </div>
          )}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>
            {autorizada ? 'Fechar' : 'Voltar e ajustar'}
          </button>
          {!autorizada && (
            <button type="button" className={styles.btnEmitir}
              onClick={onEmitir} disabled={emitindo || bloqueios.length > 0}>
              <FiSend size={14} /> {emitindo ? 'Transmitindo...' : 'Emitir NFS-e'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalRevisao;
